import React, { useEffect, useState, useRef } from "react";
import ArkEditor from "@shared/components/EditorJs/ArkEditor";
import { useParams } from "react-router-dom";
import AddComment from "@shared/components/EditorJs/comments/AddComments";
import FloatingComments from "@shared/components/EditorJs/comments/FloatingComments";
import { apiService } from "@services/ApiService";
import CommentService from "@shared/components/EditorJs/utils/commentsApi";
import CommentDeletionConfirmationModal from "@shared/components/EditorJs/comments/CommentDeletionConfirmationModal";

import {
  Comment,
  ReportEditorProps,
  SelectedBlockProps,
} from "@shared/components/EditorJs/comments/interfaces";
import { displayReportName } from "@pages/Project/utils/utils";

const ReportContentJson: React.FC<ReportEditorProps> = ({
  initialContent,
  initialTitle,
  canComment,
  created_at,
  template_name,
  isReportEditing,
}) => {
  const [selectedBlock, setSelectedBlock] = useState<SelectedBlockProps | null>(
    null
  );

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    const updateSelectedIds = (e: Event) => {
      // Skip if clicking inside an element with class "comment"
      const target = e.target as HTMLElement;
      if (target.closest(".comment")) return;

      const selectedElements = document.querySelectorAll(
        ".ce-block--selected[data-id]"
      );
      const ids = Array.from(selectedElements).map((el) =>
        el.getAttribute("data-id")
      ) as string[];
      setSelectedIds(ids);
    };

    document.addEventListener("click", updateSelectedIds);
    document.addEventListener("keyup", updateSelectedIds);

    return () => {
      document.removeEventListener("click", updateSelectedIds);
      document.removeEventListener("keyup", updateSelectedIds);
    };
  }, []);

  const [comments, setComments] = useState<Comment[]>([]);
  const { projectId, reportId } = useParams<{
    projectId: string;
    reportId: string;
  }>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteCommentId, setDeleteCommentId] = useState<string>("");
  const [isResolve, setIsResolve] = useState(false);
  const mousePosRef = useRef({ x: 0, y: 0 });

  const updateComments = () => {
    if (projectId && reportId) {
      CommentService.getComments(projectId, reportId).then((comments) => {
        setComments(comments);
      });
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mousePosRef.current = {
        x: e.clientX,
        y: e.clientY,
      };
    };

    document.addEventListener("mousemove", handleMouseMove);
    return () => document.removeEventListener("mousemove", handleMouseMove);
  }, []);

  useEffect(() => {
    if (!canComment) return;

    updateComments();

    const handleClick = (event: Event) => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return; // Prevents error
      const copy = selection;

      const range = selection.getRangeAt(0);

      const selectedText = selection.toString().trim();
      if (!selectedText) return;

      const targetElement = event.currentTarget as HTMLElement;

      // Get selection start offset within the block
      const preSelectionRange = document.createRange();
      preSelectionRange.setStart(targetElement, 0);
      preSelectionRange.setEnd(range.startContainer, range.startOffset);
      const startOffset = preSelectionRange.toString().length;
      const endOffset = startOffset + selectedText.length;

      // Store necessary details in `selectedBlock`

      setSelectedBlock({
        target: targetElement,
        selection: copy,
        startOffset,
        endOffset,
      });
    };

    const handleOutsideClick = (event: Event) => {
      const target = event.target as HTMLElement;

      // Check if the clicked element is inside a ce-block or comment-container
      if (!target.closest(".comment")) {
        const selection = window.getSelection();
        if (!selection) setSelectedBlock(null);

        if (selection) {
          const text = selection.toString().trim();
          if (!text) setSelectedBlock(null);
        } // Reset selected block
      }
    };

    const attachListeners = () => {
      const nodes = document.getElementsByClassName("ce-block");
      Array.from(nodes).forEach((node) => {
        node.addEventListener("click", handleClick);
      });
    };

    setTimeout(() => {
      attachListeners();
    }, 1000);

    // Add global click listener to detect clicks outside ce-blocks and comment-container
    document.addEventListener("click", handleOutsideClick);

    return () => {
      // Cleanup event listeners
      const nodes = document.getElementsByClassName("ce-block");
      Array.from(nodes).forEach((node) => {
        node.removeEventListener("click", handleClick);
      });

      document.removeEventListener("click", handleOutsideClick);
    };
  }, [initialContent, initialContent, projectId, reportId, canComment]);

  const handleCommentSubmit = async (comment: string) => {
    if (!projectId || !reportId) return;

    if (selectedBlock || selectedIds.length > 0) {
      let anchor = [];
      let blockId = "unknown";

      if (selectedBlock) {
        blockId = selectedBlock.target.getAttribute("data-id") || "unknown";
        const { startOffset, endOffset } = selectedBlock;
        anchor = [`${blockId}|${startOffset}|${endOffset}`];
      } else {
        anchor = selectedIds;
      }

      const body = {
        message: comment,
        anchor,
        only_author_visibility: false,
      };

      try {
        await apiService.post(
          `projects/comments/${projectId}/report/${reportId}/add/`,
          body
        );
      } catch (error) {
        console.error("Error Adding comment:", error);
        return false;
      }
    }

    updateComments();
  };

  const handleReply = async (
    comment: string,
    commentId: string,
    edit = false
  ) => {
    if (!projectId || !reportId) return;

    const body = {
      message: comment,
    };

    try {
      if (edit) {
        await apiService.patch(
          `projects/comments/${projectId}/report/${reportId}/edit/${commentId}`,
          body
        );
      } else {
        await apiService.post(
          `projects/comments/${projectId}/report/${reportId}/reply/${commentId}`,
          body
        );
      }
    } catch (error) {
      console.error("Error Adding comment:", error);
      return false;
    }

    updateComments();
  };

  const handleCommentDelete = async (commentId: string) => {
    if (!projectId || !reportId) return;

    try {
      await apiService.delete(
        `projects/comments/${projectId}/report/${reportId}/remove/${commentId}`,
        undefined,
        undefined,
        undefined,
        "Comment"
      );
    } catch (error) {
      console.error("Error Adding comment:", error);
      return false;
    }
    updateComments();
    setIsResolve(false);
  };

  const confirmDelete = () => {
    handleCommentDelete(deleteCommentId);
    setIsModalOpen(false);
  };

  const openDelModal = (commentId: string, resolve = false) => {
    setIsResolve(resolve);
    setDeleteCommentId(commentId);
    setIsModalOpen(true);
  };

  const showAddCommentPopup =
    selectedIds.length > 0 ||
    (selectedBlock && selectedBlock.target.getAttribute("data-id"));

  return (
    <div className="relative mx-auto p-7" id="reportDiv">
      <div className="max-w-[calc(100%-60px)] mx-auto">
        <h2 className="text-2xl 2xl:text-3xl font-bold mb-12">
          {displayReportName(initialTitle, template_name, created_at)}
        </h2>
      </div>

      <ArkEditor initialContent={initialContent} isReadOnly={true} />

      {canComment && !isReportEditing && (
        <>
          {/* Show Comment Component only when a block is selected */}
          {showAddCommentPopup && (
            <AddComment
              setSelectedBlock={setSelectedBlock}
              selectedBlock={selectedBlock}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              onCommentSubmit={handleCommentSubmit}
              mousePosRef={mousePosRef}
            />
          )}

          {/* Display added comments in floating manner */}
          <FloatingComments
            comments={comments}
            handleReply={handleReply}
            handleCommentDelete={openDelModal}
          />

          <CommentDeletionConfirmationModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onConfirm={confirmDelete}
            resolve={isResolve}
          />
        </>
      )}
    </div>
  );
};

export default ReportContentJson;
